import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material';
import MDSnackbar from 'components/MDSnackbar';
import MDTypography from 'components/MDTypography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import PropTypes from 'prop-types';
import MDInput from 'components/MDInput';

import PosPrint from './PosPrint';

function PosBillHistory({ setBlockScan, sizeButton, buttonLoading, setButtonLoading, lastbill }) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const txtBill = useRef('');
  const [inputSearch, setInputSearch] = useState('');
  const [modalChangeSearch, setModalChangeSearch] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [notify, setNotify] = useState(null);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const inputBill = useRef(null);
  const saleOrderId = useRef(null);
  const printRef = useRef();
  const btnRef = useRef(null);
  const denyPressed = useRef(false);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  useEffect(() => {
    inputBill.current?.focus();
  }, [modalAdd]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const printReciptHistory = (so_id) => {
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/printReciptHistory`, app.token, {
      lang: app.lang,
      so_id: so_id,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          printRef.current.print(so_id, false);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const modal = (isCash) => {
    Swal.fire({
      title: `บิล : ${txtBill.current}`,
      icon: 'success',
      showDenyButton: isCash,
      showCancelButton: true,
      confirmButtonText: 'พิมพ์ใบเสร็จ',
      confirmButtonColor: '#3085d6',
      denyButtonText: 'เงินทอน',
      denyButtonColor: '#3085d6',
      cancelButtonText: 'ปิด',
      cancelButtonColor: '#dc3545',
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: () => {
        printReciptHistory(saleOrderId.current);
        return false;
      },
      preDeny: () => {
        denyPressed.current = true;
        setModalChangeSearch(true);
        return true;
      },
      didClose: () => {
        if (!denyPressed.current) {
          txtBill.current = '';
        }
        denyPressed.current = false;
      },
    });
  };

  const close = () => {
    setInputSearch('');
    setModalAdd(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const open = () => {
    setBlockScan(true);
    setModalAdd(true);
  };

  const findSaleOrderId = (so_code = inputSearch) => {
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/getSaleOrderId`, app.token, {
      lang: app.lang,
      so_code: so_code,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          if (result.summary) setSummary(result.summary);
          saleOrderId.current = result.id;
          modal(Boolean(result.summary));
          close();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clear = () => {
    setModalChangeSearch(false);
    modal(true);
  };

  const renderChange = () => {
    return (
      <Dialog
        open={modalChangeSearch}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-change'
        fullWidth
        onClose={clear}
      >
        <DialogTitle>
          <i className='fad fa-money-bill-alt color-danger' /> &nbsp; เงินทอน
          <IconButton
            aria-label='close'
            onClick={clear}
            autoFocus
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <TableContainer sx={{ boxShadow: 0 }}>
            <Table aria-label='summary table'>
              <TableBody>
                <TableRow>
                  <TableCell align='right' width={200}>
                    <MDTypography variant='h2'>รวมทั้งสิ้น :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='info'>
                      {Func.numberDisplay(summary.nettotal)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h2'>รับเงิน :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='success'>
                      {Func.numberDisplay(summary.money_receive)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h2'>เงินทอน :</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h1' color='error'>
                      {Func.numberDisplay(summary.money_change)}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  };

  const renderInfo = () => {
    return (
      <MDBox>
        <MDBox
          noValidate
          component='form'
          role='form'
          onSubmit={(e) => {
            e.preventDefault();
            findSaleOrderId();
          }}
        >
          <MDInput
            name='searchform'
            type='search'
            fullWidth
            onChange={(e) => {
              txtBill.current = e.target.value;
              setInputSearch(e.target.value);
            }}
            value={inputSearch}
            inputRef={inputBill}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <span>เลขที่บิล :</span>
                </InputAdornment>
              ),
              autoComplete: 'off',
            }}
          />
          <MDButton
            fullWidth
            variant='outlined'
            color='info'
            sx={{ mt: 2, fontSize: 18 }}
            onClick={() => {
              txtBill.current = lastbill;
              setInputSearch(lastbill);
              findSaleOrderId(lastbill);
            }}
          >
            บิลล่าสุด : {lastbill}
          </MDButton>
        </MDBox>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          <i className='fad fa-file-invoice color-danger' /> &nbsp; ใบเสร็จย้อนหลัง
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderInfo()}</DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={close}>
                <Icon>close</Icon>&nbsp;ยกเลิก
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton
                variant='text'
                color='info'
                onClick={() => findSaleOrderId()}
                disabled={loading}
              >
                {loading ? <CircularProgress size={18} color='inherit' /> : <Icon>search</Icon>}
                &nbsp;ค้นหา
              </MDButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <Tooltip title='Sales'>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={open}
          ref={btnRef}
          disabled={buttonLoading || !lastbill}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-file-invoice' />
          )}
          &nbsp; ใบเสร็จย้อนหลัง
        </MDButton>
      </Tooltip>
      {renderChange()}
      {notify}
      {renderAdd()}

      <PosPrint ref={printRef} />
    </MDBox>
  );
}

PosBillHistory.propTypes = {
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
  lastbill: PropTypes.string.isRequired,
};

export default PosBillHistory;
